import React, {useMemo} from 'react';

import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {NoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import preserveShallowEquality from 'util/preserveShallowEquality';

import TableWithMissingSchema from './components/TableWithMissingSchema';
import {UnknownStructuredTableAdapter} from './StructuredTableConstants';
import StructuredTableForm from './StructuredTableForm';
import {NoteLinkedStructuredTable} from './StructuredTablePlugin';
import StructuredTableView, {StructuredTableDefaultTableEditableProps} from './StructuredTableView';
import {doesUserHavePermissionToViewTable} from './util/doesUserHavePermissionToViewTable';

/**
 * A simple pass-through component whose only purpose is to branch between
 *     StructuredTableView (for tables rendered normally), and
 *     StructuredTableForm (for single-row tables rendered as a form view)
 */

interface StructuredTableFormOrViewProps<TGroupMetadata = unknown> extends MediocreSearchResultProps {
  editor: NoteEditor;
  notePart: NotePart & {item: NoteLinkedStructuredTable};
  adapter: UnknownStructuredTableAdapter;
  editorProps: NoteEditorProps<NotePart | NoteLinkedGroup<TGroupMetadata>>;
  index: number;
  itemProps: TableItemProps & {forceLiveness?: number};
  onUpdateContent?: (index: number, content: object, selectors?: never, tableOp?: TableOpDiffMap) => void;
  isInGroup?: boolean;
  deleteHandler?: () => void;
  overrideDefaultTableProps?: Partial<StructuredTableDefaultTableEditableProps>;
  /**
   * Threaded only to the View path (not StructuredTableForm) -- see
   * StructuredTableView.onLiveStalenessChange for why this callback exists.
   */
  onLiveStalenessChange?: (hasStaleCells: boolean) => void;
}

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
}

class StructuredTableFormOrViewConnector extends DataConnector<
  StructuredTableFormOrViewProps,
  ConnectedProps
> {
  override stores = [SchemaFieldSelectorStore];

  override shouldSubscribeToRedux = false;

  enhanceProps: () => ConnectedProps;

  constructor(props: StructuredTableFormOrViewProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  uncachedEnhanceProps = (): ConnectedProps => {
    const selectorIds = getSelectorIdsFromFields(
      this.props.notePart.item.schemas.flatMap((schema) => schema.fields)
    );
    if (selectorIds.length === 0) {
      return {
        selectorsById: {},
      };
    }
    const selectorsById = this.getOrFetchMultipleOptionalFull(
      ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
      selectorIds
    );
    return {
      selectorsById,
    };
  };
}

function UnconnectedStructuredTableFormOrView(
  props: StructuredTableFormOrViewProps & ConnectedProps
): JSX.Element {
  const {editor, notePart, editorProps, adapter, index, itemProps, selectorsById} = props;
  const {item: structuredTable} = notePart;
  const {name} = structuredTable;

  const icon = adapter.getTableIcon(structuredTable);
  const indentation = editor.getIndentation(notePart);
  const tableName = name;

  const basicTableProps = useMemo(() => ({icon, indentation, tableName}), [icon, indentation, tableName]);

  if (adapter.shouldShowAsForm(structuredTable)) {
    return (
      <StructuredTableForm
        notePart={notePart}
        itemProps={itemProps}
        editorProps={editorProps}
        index={index}
        updateHandler={props.onUpdateContent ?? editor.itemUpdateHandler}
        isInGroup={props.isInGroup}
        adapter={adapter}
        {...forwardSearchResultProps(props)}
      />
    );
  }

  if (!doesUserHavePermissionToViewTable(structuredTable)) {
    return <TableWithMissingSchema {...basicTableProps} />;
  }

  return (
    <StructuredTableView
      // Its hooks depend on the table's adapter, so a different table must remount it. Not `cid`: action
      // functions refresh the entry and regenerate cids.
      key={structuredTable.provenanceUuid}
      basicTableProps={basicTableProps}
      editor={editor}
      editorProps={editorProps}
      notePartCid={editorProps.getCid(notePart)}
      index={index}
      itemProps={itemProps}
      structuredTable={structuredTable}
      structuredTableAdapter={adapter}
      mouseDownHandler={props.isInGroup ? undefined : editor.itemMouseDownHandler}
      mouseOverHandler={props.isInGroup ? undefined : editor.itemMouseOverHandler}
      updateHandler={props.onUpdateContent ?? editor.itemUpdateHandler}
      selectorsById={selectorsById}
      deleteHandler={props.deleteHandler}
      overrideDefaultTableProps={props.overrideDefaultTableProps}
      onLiveStalenessChange={props.onLiveStalenessChange}
      {...forwardSearchResultProps(props)}
    />
  );
}

const StructuredTableFormOrView = StructuredTableFormOrViewConnector.connect(
  UnconnectedStructuredTableFormOrView
);

export default StructuredTableFormOrView;
